import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/api/client';
import { qk, researchRunsQuery, snapshotsQuery } from '@/api/queries';
import { multipart } from '@/api/multipart';
import type { AuditEntry, Organization, OrganizationSummary, Run, SubscriptionOut } from '@/api/types';

export type OrgListParams = { search?: string; status?: 'active' | 'trial' | 'archived'; plan?: string; pm?: string };

export const organizationsPageQuery = (params: OrgListParams) =>
  queryOptions({
    queryKey: qk.organizations({ ...params, size: 100 }),
    queryFn: () => unwrap(api.GET('/admin/organizations', { params: { query: { ...params, size: 100 } } })),
    select: (p) => ({ items: p.items as OrganizationSummary[], total: p.total }),
    placeholderData: keepPreviousData,
  });

/** The subscription with its plan usage (GAP: the API has no usage numbers yet; fixtures fill `usage`). */
export type Subscription = SubscriptionOut & { usage?: Record<string, number> };

/** The subscription in force: active or trial, the newest first (the API lists newest first). */
export function currentSubscription(list: Subscription[]): Subscription | null {
  return list.find((x) => x.status === 'active' || x.status === 'trial') ?? null;
}

/** The responsible PM: the owner_pm member, else the first PM. */
export function ownerOf(org: Organization) {
  return org.members.find((m) => m.member_role === 'owner_pm')?.user ?? org.pms[0] ?? null;
}

export const orgSubscriptionsQuery = (orgId: string) =>
  queryOptions({
    queryKey: qk.orgSubscriptions(orgId),
    queryFn: async (): Promise<Subscription[]> => unwrap(api.GET('/admin/organizations/{org_id}/subscriptions', { params: { path: { org_id: orgId } } })),
  });

export const orgActivityQuery = (orgId: string) =>
  queryOptions({
    queryKey: qk.orgActivity(orgId),
    queryFn: () => unwrap(api.GET('/admin/organizations/{org_id}/activity', { params: { path: { org_id: orgId }, query: { size: 50 } } })),
    select: (p) => p.items as AuditEntry[],
  });

/** The newest research run (its sources carry the live statuses of the organization's sources). */
export const latestRunQuery = (orgId: string) => ({ ...researchRunsQuery(orgId), select: (runs: Run[]) => runs[0] ?? null });

export { snapshotsQuery };

export type TelegramChannel = Schemas['TelegramChannelOut'];

/** The shortest text the Telegram field searches for (the API takes 2; 3 keeps the account's searches few). */
export const TELEGRAM_MIN_QUERY = 3;

/**
 * GET /admin/telegram/channels?q= — the public Telegram channels matching a name, over MTProto. Never retried:
 * 503 `telegram_search_unavailable` and 429 `rate_limited` are answers (the field still takes a link).
 */
export const telegramChannelsQuery = (q: string) =>
  queryOptions({
    queryKey: ['telegram-channels', q] as const,
    queryFn: () => unwrap(api.GET('/admin/telegram/channels', { params: { query: { q } } })),
    enabled: q.length >= TELEGRAM_MIN_QUERY,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    retry: false,
  });

export function createOrganization(body: Schemas['OrganizationCreate']): Promise<Organization> {
  return unwrap(api.POST('/admin/organizations', { body }));
}

export function updateOrganization(id: string, body: Schemas['OrganizationPatch']): Promise<Organization> {
  return unwrap(api.PATCH('/admin/organizations/{org_id}', { params: { path: { org_id: id } }, body }));
}

export function putMembers(id: string, members: Schemas['MemberIn'][]): Promise<Organization> {
  return unwrap(api.PUT('/admin/organizations/{org_id}/members', { params: { path: { org_id: id } }, body: { members } }));
}

/** POST /admin/organizations/{id}/media (multipart: file + purpose). */
export async function uploadMedia(orgId: string, file: File, purpose: Schemas['MediaPurpose']) {
  const { body, contentType } = await multipart({ purpose, file });
  return unwrap(
    api.POST('/admin/organizations/{org_id}/media', {
      params: { path: { org_id: orgId } },
      body: { file: '', purpose },
      bodySerializer: () => body as unknown as BodyInit,
      headers: { 'Content-Type': contentType },
    }),
  );
}

export function assignPlan(orgId: string, body: Schemas['SubscriptionCreate']) {
  return unwrap(api.POST('/admin/organizations/{org_id}/subscriptions', { params: { path: { org_id: orgId } }, body }));
}

export function patchSubscription(id: string, body: Schemas['SubscriptionPatch']) {
  return unwrap(api.PATCH('/admin/subscriptions/{sub_id}', { params: { path: { sub_id: id } }, body }));
}
